/* Formatting helpers.
 *
 * Rule: values arrive as decimal strings and are formatted for DISPLAY only.
 * We never round-trip a price through a JavaScript number and back — that is
 * how a 2841.50 becomes a 2841.4999999999995.
 */

const localeFor = (currency: string) =>
  currency === 'INR' ? 'en-IN' : 'en-US'

/** Format a decimal string for display, preserving the value's precision. */
export function fmtPrice(value: string | number | null | undefined, dp = 2): string {
  if (value === null || value === undefined || value === '') return '—'
  const n = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(n)) return String(value)
  return n.toLocaleString('en-US', {
    minimumFractionDigits: dp,
    maximumFractionDigits: dp,
  })
}

export function fmtQty(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—'
  const n = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(n)) return String(value)
  // Whole lots are the common case; only show decimals when there are any.
  return Number.isInteger(n)
    ? n.toLocaleString('en-US')
    : n.toLocaleString('en-US', { maximumFractionDigits: 4 })
}

export function fmtMoney(
  value: string | number | null | undefined,
  currency = 'INR',
  compact = false,
): string {
  if (value === null || value === undefined || value === '') return '—'
  const n = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(n)) return String(value)

  if (compact && Math.abs(n) >= 1000) {
    // Indian numbering uses lakh and crore; using K/M/B for an INR account is
    // technically correct and reads wrong to the people using this screen.
    if (currency === 'INR') {
      if (Math.abs(n) >= 1e7) return `${sign(n)}₹${(Math.abs(n) / 1e7).toFixed(2)} Cr`
      if (Math.abs(n) >= 1e5) return `${sign(n)}₹${(Math.abs(n) / 1e5).toFixed(2)} L`
      return `${sign(n)}₹${(Math.abs(n) / 1e3).toFixed(1)}K`
    }
    if (Math.abs(n) >= 1e9) return `${sign(n)}${symbol(currency)}${(Math.abs(n) / 1e9).toFixed(2)}B`
    if (Math.abs(n) >= 1e6) return `${sign(n)}${symbol(currency)}${(Math.abs(n) / 1e6).toFixed(2)}M`
    return `${sign(n)}${symbol(currency)}${(Math.abs(n) / 1e3).toFixed(1)}K`
  }

  return n.toLocaleString(localeFor(currency), {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

function sign(n: number): string {
  return n < 0 ? '-' : ''
}

function symbol(currency: string): string {
  const map: Record<string, string> = {
    INR: '₹', USD: '$', EUR: '€', GBP: '£', JPY: '¥',
    SGD: 'S$', HKD: 'HK$', AUD: 'A$',
  }
  return map[currency] ?? `${currency} `
}

/** Signed percentage, always with an explicit + or -. */
export function fmtPct(value: string | number | null | undefined, dp = 2): string {
  if (value === null || value === undefined || value === '') return '—'
  const n = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(n)) return String(value)
  return `${n >= 0 ? '+' : ''}${n.toFixed(dp)}%`
}

/** Signed value for P&L. The sign is shown explicitly so direction never
 *  depends on colour alone. */
export function fmtSigned(value: string | number | null | undefined, dp = 2): string {
  if (value === null || value === undefined || value === '') return '—'
  const n = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(n)) return String(value)
  return `${n > 0 ? '+' : ''}${n.toLocaleString('en-US', {
    minimumFractionDigits: dp,
    maximumFractionDigits: dp,
  })}`
}

export type Direction = 'up' | 'down' | 'flat'

export function direction(value: string | number | null | undefined): Direction {
  if (value === null || value === undefined || value === '') return 'flat'
  const n = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(n) || n === 0) return 'flat'
  return n > 0 ? 'up' : 'down'
}

export function fmtTime(ts: string | number | Date): string {
  const d = ts instanceof Date ? ts : new Date(ts)
  return d.toLocaleTimeString('en-GB', { hour12: false })
}

export function fmtDateTime(ts: string | number | Date): string {
  const d = ts instanceof Date ? ts : new Date(ts)
  return `${d.toLocaleDateString('en-GB')} ${d.toLocaleTimeString('en-GB', { hour12: false })}`
}

export function fmtLatencyNs(ns: number | null | undefined): string {
  if (ns === null || ns === undefined || !Number.isFinite(ns)) return '—'
  if (ns < 1_000) return `${ns} ns`
  if (ns < 1_000_000) return `${(ns / 1_000).toFixed(1)} µs`
  return `${(ns / 1_000_000).toFixed(2)} ms`
}

/** True when a quote's source INVENTS prices rather than observing them.
 *
 *  Mirrors `is_simulated()` in the API. Kept as one exported function because
 *  the check was previously written inline in two panels as "does not start
 *  with UPSTOX" — a rule that silently became wrong the moment the Upstox feed
 *  gained a second venue prefix (UPSTOX_BSE), and would have mislabelled real
 *  SENSEX levels as simulated. */
const SIMULATED_SOURCES = ['RANDOM_WALK', 'REPLAY', 'SIMULATOR']

export function isSimulatedSource(source: string | null | undefined): boolean {
  if (!source) return false
  return SIMULATED_SOURCES.some((s) => source.startsWith(s))
}
